import { isEmpty } from "lodash";
import { Constant, rootModuleId } from '@app/const';
import { defaultServiceContext } from '@app/contexts';
import VariableDataModel from "@app/models/VariableDataModel";
import {
    defaultLanguage,
    defaultVoiceTalent,
    Design,
    DesignChannelType,
    DesignClass,
    DesignVariable,
    DtmfValue,
    ExitObjectOutputType,
    FunctionType,
    NodeTypeName,
    ObjectClass,
    UserFeatureFlag,
    VariableType
} from "@app/types";
import CommonModuleManager from "./CommonModuleManager";
import { createUuid } from "./functions";
import NodeRegistry from "./NodeRegistry";
import Seed from './Seed';
import ModuleUsedIn from "./usedIn/ModuleUsedIn";
import ObjectsUsedIn from "./usedIn/ObjectsUsedIn";
import Utility from "./Utility";
import WizardConfig from "./WizardConfig";

/**
 * JSDoc imports.
 *
 * @import { LegacyOption } from '@app/types';
 */

const {
    featureFlagService,
    lokiService
} = defaultServiceContext;

export default class KnowledgeBase {
    constructor() {
        this.wizardConfig = new WizardConfig();
        this.commonModuleManager = new CommonModuleManager();
    }

    /**
     * Add CCXML lastLocalChange timestamp to serialized object
     * 
     * @param {object} doc 
     */
    initializeCCXMLTimestamp(doc) {
        if (!doc['ccxmlLastLocalChange']) {
            doc['ccxmlLastLocalChange'] = doc.lastSavedToRepo;
        }
    }

    /**
     * Callback once documents are loaded.
     *
     * @param {Design} serialized Serialized app model.
    /** Load app. */
    async loadTree() {
        const { branchId } = window.apm;
        const serialized = await lokiService.getSerialized(branchId);
        if (!serialized) {
            window.location = "/";
            throw new Error(`Branch ID '${branchId}' not found.`);
        }

        this.initializeCCXMLTimestamp(serialized);
        await this.databaseLoaded(serialized);
        return serialized;
    }

    /**
     * Callback once documents are loaded.
     *
     * @param {Design} serialized Serialized app model.
     */
    async databaseLoaded(serialized) {
        const appManager = window.apm;
        appManager.branchId = serialized.branchId ?? serialized._id;
        appManager.serialized = await this.normalizeSerialized(serialized);
        // TODO: remove by 2021-11-15
        // hot-fix for PGE reported in slack: https://interactions-connect.slack.com/archives/C97TU7F7T/p1632771585085200
        this.fixMissingPromptSpanishText(serialized);
        this.commonElements = this.normalizeCommonElements(Seed.getElements());
        appManager.config.initLayoutConfig();
        appManager.reactDOMComponents.renderApplicationPages();
        document.title = serialized.client + " " + serialized.application + " " + serialized.repoVersion;
        // TODO: remove some time in the future
        this.removeFromLocalAndSessionStoragePendo();
    }

    fixMissingPromptSpanishText(serialized) {
        const promptObjects = serialized.components.objects.filter(obj => obj.props.nodeTypeName === NodeTypeName.prompt);
        const comoPromptObjects = promptObjects.filter(p => p.isHidden);

        comoPromptObjects.forEach(comoPrompt => {    
            const usedInModules = ObjectsUsedIn.getObjectUsedIn(comoPrompt.id, serialized);
            
            if(usedInModules[0][0] && usedInModules[0][0].length > 0){
                const flowPrompt = serialized.components.modules.find(mod => mod.id === usedInModules[0][0]).versions['1'].flow.nodes
                    .filter(node => node.props.parentComponentID === comoPrompt.id)[0];
                
                const flowPromptHasSpanishText = flowPrompt.props.config.text && flowPrompt.props.config.text['es-US'];
                const comoPromptObjectHasSpanishText = comoPrompt.props.config.text && comoPrompt.props.config.text['es-US']

                if(flowPromptHasSpanishText && !comoPromptObjectHasSpanishText){
                    // TODO: remove console.log by 2021-11-15 
                    console.log(comoPrompt.name)
                    comoPrompt.props.config.text['es-US'] = flowPrompt.props.config.text['es-US'];
                    comoPrompt.props.lastLocalChange = window.apm.timestamp;
                }
            }
        });
    }

    normalizeD1Import(serialized) {
        const rootModule = serialized.components.modules.find(mod => mod.id === rootModuleId);

        rootModule.props.config = rootModule.props.config || {};

        serialized.application = serialized.application.replace(" ", "");

        if(serialized.fillerPrompts.length == 0) {
            serialized.fillerPrompts = {
                "typing": [
                    {
                        "name": "SHORT_TYPING",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "{typing}."
                            }
                        ]
                    },
                    {
                        "name": "LONG_TYPING",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "{typing} {typing} {typing}."
                            }
                        ]
                    }
                ],
                "wait": [
                    {
                        "name": "FILLER_1",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Hold on for just a moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_2",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Hold one moment, please."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_3",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Please hold for one moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_4",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "One moment, please."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_5",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Bear with me just a moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_6",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Please hold for one moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_7",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Hold on just one moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_8",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Please hang on for just a moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_9",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Please hold for one moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_10",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Please give me a moment."
                            }
                        ]
                    },
                    {
                        "name": "FILLER_11",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "Bear with me just one moment, please."
                            }
                        ]
                    }
                ],
                "data_access": [
                    {
                        "name": "WAIT_AUDIO",
                        "transcripts": [
                            {
                                "locale": "en-US",
                                "transcript": "{add 2.5sec silence} {typing} {typing} {typing} One moment, please. {typing} {typing} {typing} Please hold on. {typing} Please give me a moment. {typing} {typing} {typing}"
                            }
                        ]
                    }
                ]
            }
        }

        return serialized;
    }

    /**
     * Normalize app serialized data shape
     * @param {Design} serialized
     * @return {*}
     */
    async normalizeSerialized(serialized) {
        // CORE-33685: Added hydration of designs to normalize serialized
        const {
            avsService,
            intentService
        } = defaultServiceContext;

        await Promise.all([
            avsService.hydrateDesign(serialized), 
            intentService.hydrateDesign(serialized)
        ]);

        avsService.normalizeSerialized(serialized);

        serialized.components.variables.forEach((variable) => {
            this.normalizeVariable(variable);
        });

        serialized.omniChannel ??= {};
        serialized.omniChannel.isOmniChannel ??= false;
        serialized.omniChannel.lastLocalChange ??= 0;

        // applications marked for D1 imports
        if(serialized.client === "Progressive"){
            serialized = this.normalizeD1Import(serialized);
        }

        const isCommonModule = Utility.getIsCommonModule(serialized); 
        
        // legacy issue: Common Modules did not get `lastSavedToRepo` updated on save
        if( isCommonModule && !serialized.lastSavedToRepo){
            serialized.lastSavedToRepo = serialized.lastUpdate;
        }
        
        // Common Modules initialize sensitivity overrides
        if(isCommonModule && !serialized.sensitiveOverrides){
            this.commonModuleManager.initializeSensitivityOverrides(serialized);
        }
        
        if (!serialized.hasOwnProperty("globalErrorHandling")) {
            serialized.globalErrorHandling = {
                errorHandling: "c2fd95fa-8b5f-4bd5-bd92-4fe3ca784ddb",
                postProcessingCustom: null,
                sessionStart: "e9676ff6-cc92-4fcb-9b62-482eebc77da4",
                userHangUp: "8be14106-6031-47ac-a794-1774a5495d52"
            };
        }
        if (!serialized.hasOwnProperty("globalExceptionHandling")) {
            serialized.globalExceptionHandling = {
                "exception": "f414d315-af49-4723-a16f-e9aeaa2df6e1",
                "timeout": "802a9bde-80b8-429c-8cba-d9d21def31a0"
            };
        }
        if (!serialized.hasOwnProperty("appCommonModules")) {
            serialized.appCommonModules = [];
        }
        if (!serialized.hasOwnProperty("appProperties")) {
            serialized.appProperties = [];
        }

        // TODO: Should update appProperties here vs. in the CallEncryption component's constructor
        // if(!serialized.appProperties.find(p => p.type === "callEncryption")){
        //     const callEncryption = {
        //         id: createUuid(),
        //         type: "callEncryption",
        //         config: "Full Call Encryption",
        //         enabled: false,
        //         pubKeyNameProperty: null,
        //         pubKeyUrlProperty: null,
        //         recordSensitiveUtterances: false,
        //         lastLocalChange: window.apm.timestamp
        //     };
        //     serialized.appProperties.push(callEncryption);
        // }


        if (!serialized.hasOwnProperty("dtmf_live_agent_override_pattern")) {
            serialized.dtmf_live_agent_override_pattern = "";
            serialized.dtmf_live_agent_override_patternLastLocalChange = window.apm.timestamp;
        }
        
        if (!serialized.hasOwnProperty("unitTests") || serialized.unitTests === 'undefined') {
            serialized.unitTests = [];
        }

    
        // DEVDES2-1021: initialize UI display prefs in serialized
        if(!serialized.hasOwnProperty('displayPrefs')){
            serialized.displayPrefs = {};
            // show/hide disabled elements
            serialized.displayPrefs['hideDisabledElements'] = serialized.displayPrefs['hideDisabledElements'] || false;
        }
        //fix GlobalIntents
        if(serialized.globalIntents){
            serialized.globalIntents.forEach(gl => {
                if (!gl.id) {
                    gl.id = createUuid();
                    gl.lastLocalChange = window.apm.timestamp;
                }
                if (!gl.moduleProperties) {
                    var moduleIndex = serialized.components.modules.findIndex(mo => mo.id === gl.module);
                    if (moduleIndex !== -1) {
                        gl.moduleProperties = {};
                        gl.moduleProperties.id = createUuid();
                        gl.moduleProperties.name = serialized.components.modules[moduleIndex].name;
                        gl.moduleProperties._class = DesignClass.module;
                        gl.moduleProperties.type = "module";
                        gl.moduleProperties.props = serialized.components.modules[moduleIndex].props;
                        gl.moduleProperties.props.ref = serialized.components.modules[moduleIndex].id;
                        gl.moduleProperties.props.parentComponentID = serialized.components.modules[moduleIndex].id;
                        gl.lastLocalChange = window.apm.timestamp;
                    }
                };
                if (!gl.conditionProperties) {
                    var conditionProps = {
                        "id": createUuid(),
                        "_class": DesignClass.condition,
                        "props": {
                            "nodeTypeName": NodeTypeName.condition,
                            "colorClass": "primary",
                            "config": {
                                "name": "",
                                "condition": [],
                                description: "",
                                inputs: [],
                                intent:gl.intent,
                                serializedCondition: ""
                            }
                        }
                    };
                    gl.conditionProperties = conditionProps;
                    gl.lastLocalChange = window.apm.timestamp;
                }

                //CORE-36247: removed global dtmf from global intents
                if(gl.conditionProperties.props.config.dtmfInput){
                    gl.lastLocalChange = window.apm.timestamp;
                    delete gl.conditionProperties.props.config.dtmfInput;
                    delete gl.conditionProperties.props.config.inputType;
                }
                // look through globlaIntents and mark localvariable as module input accordingly
                if (gl.moduleProperties && gl.moduleProperties.props.config.inputs) {
                    const mod = serialized.components.modules.find(m => m.id === gl.module);
                    gl.moduleProperties.props.config.inputs.forEach(i => {
                        if (mod && mod.localVariables) {
                            const lvIndex = mod.localVariables.findIndex(v => v.name === i.argumentName.split('mod.')[1])
                            
                            if (lvIndex === -1) {
                                const iIndex = gl.moduleProperties.props.config.inputs.findIndex(v => v.name === i.argumentName);
                                delete gl.moduleProperties.props.config.inputs[iIndex];
                                gl.moduleProperties.props.lastLocalChange = window.apm.timestamp;
                            }
                        }
                    })
                    if (mod && mod.localVariables) {
                        mod.localVariables.forEach(lv => {
                            const defaultValueFromLocalVariable = window.apm.getDefaultValueFromLocalVariable(lv);

                            var varIndex = gl.moduleProperties.props.config.inputs.findIndex(input => input.argumentName === `mod.${lv.props.config.varName}`);
                            if (lv.props.config.moduleInput && varIndex === -1) {
                                 gl.moduleProperties.props.config.inputs.push({
                                    argumentName: `mod.${lv.props.config.varName}`,
                                    type: "value",
                                    value: defaultValueFromLocalVariable,
                                    defaultValue: defaultValueFromLocalVariable,
                                    varName: null,
                                    sensitiveData: lv.props.config.sensitiveData ? lv.props.config.sensitiveData : false
                    
                                 });
                                gl.moduleProperties.props.lastLocalChange = window.apm.timestamp;
                            }
                            else if (varIndex !== -1) {
                                const equalsAsFalsey = this.getEqualsAsFalsey(gl.moduleProperties.props.config.inputs[varIndex].defaultValue, defaultValueFromLocalVariable)
                                
                                if(!equalsAsFalsey){
                                    gl.moduleProperties.props.config.inputs[varIndex].defaultValue = defaultValueFromLocalVariable
                                    gl.moduleProperties.props.lastLocalChange = window.apm.timestamp;
                                }
                                // hot-fix for boolean inputs with string values
                                if(lv.props.config.type === 'BOOLEAN'){
                                    if(typeof (gl.moduleProperties.props.config.inputs[varIndex].value) === 'string'){
                                        if(gl.moduleProperties.props.config.inputs[varIndex].value === 'false'){
                                            gl.moduleProperties.props.config.inputs[varIndex].value = false;
                                            gl.moduleProperties.props.lastLocalChange = window.apm.timestamp;
                                        } 
                                        else if(gl.moduleProperties.props.config.inputs[varIndex].value === 'true'){
                                            gl.moduleProperties.props.config.inputs[varIndex].value = true;
                                            gl.moduleProperties.props.lastLocalChange = window.apm.timestamp;
                                        }
                                        else{
                                            gl.moduleProperties.props.config.inputs[varIndex].value = defaultValueFromLocalVariable;
                                            gl.moduleProperties.props.lastLocalChange = window.apm.timestamp;
                                        }
                                        
                                    }
                                }
                            }
                        });
                    }
                }
            });
        };

        /** DEVDES2-1528 fix intentHandling excluded lists regardless of initialization time */
        const exemptedIntentsNames = ['YES', 'NO', 'WAIT', 'BACK_FROM_WAIT', 'DTMF'];
        const exemptedIntentsIds = serialized.components.intents
            .filter(intent => exemptedIntentsNames.includes(intent.name))
            .map(intent => intent.id);
        const hasInARowExcludedProp = serialized.intentHandling.hasOwnProperty('inARowExcluded');
        const hasXAndOutExcludedProp = serialized.intentHandling.hasOwnProperty('xAndOutExcluded');

        if (hasInARowExcludedProp && Array.isArray(serialized.intentHandling.inARowExcluded)) {
            serialized.intentHandling.inARowExcluded =  serialized.intentHandling.inARowExcluded.filter(intentId => !exemptedIntentsIds.includes(intentId));
        }

        if (hasXAndOutExcludedProp && Array.isArray(serialized.intentHandling.xAndOutExcluded)) {
            serialized.intentHandling.xAndOutExcluded =  serialized.intentHandling.xAndOutExcluded.filter(intentId => !exemptedIntentsIds.includes(intentId));
        }
        /** Ends DEVDES2-1528 */

        /** Intent Handling REFACTOR
         * Init `inARowLimitLastLocalChange` and `xAndOutLimitLastLocalChange` if properties are undefined 
         */
        if (!serialized.intentHandling.hasOwnProperty('inARowLimitLastLocalChange')) {
            serialized.intentHandling.inARowLimitLastLocalChange = serialized.intentHandling.lastLocalChange ?? window.apm.timestamp;
        }

        if (!serialized.intentHandling.hasOwnProperty('xAndOutLimitLastLocalChange')) {
            serialized.intentHandling.xAndOutLimitLastLocalChange = serialized.intentHandling.lastLocalChange ?? window.apm.timestamp;
        }
        /** */

        /**
         * If `inARowExcluded` or `xAndOutExcluded` exist under `intentHandling` property
         * then check if `inARow` & `xAndOut` are already defined on each intent.
         * In other words, if either one `excluded` prop exists
         * then the refactor has not been yet applied to `intentHandling`.
        */
        if (hasInARowExcludedProp || hasXAndOutExcludedProp) {
            // set intent's inARow and xAndRow property accordingly (excluded -> false; watched -> true).
            const { inARowExcluded, xAndOutExcluded } = serialized.intentHandling;
            const inARowExcludedSet = new Set((Array.isArray(inARowExcluded)) ? inARowExcluded : []);
            const xAndOutExcludedSet = new Set(Array.isArray(xAndOutExcluded) ? xAndOutExcluded : []);

            serialized.components.intents.forEach(intent => {
                if (hasInARowExcludedProp) {
                    intent.props.config.inARow = !inARowExcludedSet.has(intent.id);
                    intent.props.lastLocalChange = window.apm.timestamp;
                }

                if (hasXAndOutExcludedProp) {
                    intent.props.config.xAndOut = !xAndOutExcludedSet.has(intent.id);
                    intent.props.lastLocalChange = window.apm.timestamp;
                }
            });

            // delete the followign deprecated `intentHandling` properties:
            delete serialized.intentHandling.inARowExcluded;
            delete serialized.intentHandling.xAndOutExcluded;
            delete serialized.intentHandling.lastLocalChange;
        }
        /** Intent Handling REFACTOR - End */

        // fix for local variables to support new module Inputs UI 
        serialized.components.modules.forEach(mo => {
            const modUsedInFlows = ModuleUsedIn.getModuleUsedIn(mo, serialized);
            // modUsedInFlows['usedInFlows'].forEach(modId => {
            //     const usedInMod = serialized.components.modules.find(mod => mod.id === modId);
            //     const { nodes } = usedInMod.versions[1].flow;
            //     // if an input matches a localVariable, the localVariable must be a module input
            //     nodes.forEach(n => {
            //         if (n.props.parentComponentID === mo.id) {
            //             if (n.props.config.inputs) {
            //                 mo.localVariables.forEach(lv => {
            //                     var varIndex = n.props.config.inputs.findIndex(input => input.argumentName === `mod.${lv.props.config.varName}`);
            //                     const input = n.props.config.inputs[varIndex];
            //                     if(varIndex !== -1){

            //                         if (input.props.config.moduleInput !== lv.props.config.moduleInput) {
            //                             input.props.config.moduleInput === lv.props.config.moduleInput
            //                             n.props.lastLocalChange = window.apm.timestamp;
            //                         }
            //                     }
            //                 });
            //             }
            //         }
            //     });
            // });

            // // TODO: commenting this block out as it it causing issues with existing module inputs
            // modUsedInFlows['usedInFlows'].forEach(modId => {
            //     const usedInMod = serialized.components.modules.find(mod => mod.id === modId);
            //     const { nodes } = usedInMod.versions[1].flow;
            //     nodes.forEach(n => {
            //         if (n.props.parentComponentID === mo.id) {
            //                 n.props.config.inputs = n.props.config.inputs || [];
            //                 mo.localVariables.forEach(lv => {
            //                     var varIndex = n.props.config.inputs.findIndex(input => input.argumentName === `mod.${lv.props.config.varName}`);
            //                     // if input cannot be matched and localVariable is a moduleInput, add a variable to the inputs list
            //                     if (lv.props.config.moduleInput && varIndex === -1) {
            //                         n.props.config.inputs.push({
            //                             id: lv.id || createUuid(),
            //                             argumentName: `mod.${lv.props.config.varName}`,
            //                             type: "value",
            //                             value: window.apm.getDefaultValueFromLocalVariable(lv),
            //                             defaultValue: window.apm.getDefaultValueFromLocalVariable(lv),
            //                             varName: null,
            //                             sensitiveData: lv.props.config.sensitiveData ? lv.props.config.sensitiveData : false
            //                         });
            //                         n.props.lastLocalChange = window.apm.timestamp;
            //                     }
            //                     else if (varIndex !== -1) {
            //                         // if input is found test/fix defaultValue
            //                         const equalsAsFalsey = this.getEqualsAsFalsey(n.props.config.inputs[varIndex].defaultValue, window.apm.getDefaultValueFromLocalVariable(lv));

            //                         if(!equalsAsFalsey){
            //                             n.props.config.inputs[varIndex].defaultValue = window.apm.getDefaultValueFromLocalVariable(lv)
            //                             n.props.lastLocalChange = window.apm.timestamp;
            //                         }
            //                         // test/fix boolean inputs with string values
            //                         if(lv.props.config.type === 'BOOLEAN'){
            //                             if(typeof (n.props.config.inputs[varIndex].value) === 'string'){
            //                                 if(n.props.config.inputs[varIndex].value === 'false'){
            //                                     n.props.config.inputs[varIndex].value = false;
            //                                     n.props.lastLocalChange = window.apm.timestamp;
            //                                 } 
            //                                 else if(n.props.config.inputs[varIndex].value === 'true'){
            //                                     n.props.config.inputs[varIndex].value = true;
            //                                     n.props.lastLocalChange = window.apm.timestamp;
            //                                 }
            //                                 else{
            //                                     n.props.config.inputs[varIndex].value = window.apm.getDefaultValueFromLocalVariable(lv);
            //                                     n.props.lastLocalChange = window.apm.timestamp;
            //                                 }
                                            
            //                             }
            //                         }
            //                     }
            //                 });

            //                 n.props.config.inputs.forEach(input => {
            //                     const localVarIndex = mo.localVariables.findIndex(lv => lv.name === input.argumentName.split('mod.')[1]);
                                
            //                     // ???
                                
            //                     if (localVarIndex === -1) {
            //                         const inputIndex = n.props.config.inputs.findIndex(inp => inp.argumentName === input.argumentName);
            //                         console.error("weird localVar input stuff")
            //                         n.props.config.inputs.splice(inputIndex, 1);
            //                     }  
            //                 });
            //         };
            //     });
            // })
        });
        //remove usedIn for localVariables
        if (serialized.components.modules) {
            serialized.components.modules.forEach(mo => {
                if (mo.localVariables && mo.localVariables.length > 0) {
                    mo.localVariables.forEach(localVariable => {
                        
                        // hot-fix Frontier: all lvs of type="variable" should be 'STRING'
                        if(localVariable.props.config.type === 'variable'){
                            localVariable.props.config.type = 'STRING';
                            delete localVariable.props.config.defalutValue;
                            mo.props.lastLocalChange = window.apm.timestamp;
                        }
                        
                        mo.props.config.inputs= mo.props.config.inputs ? mo.props.config.inputs : [];
                        
                        var moduleInputsIndex = mo.props.config.inputs.findIndex(input => input.argumentName === `mod.${localVariable.props.config.varName}`);
                        // if there is not an input to match a modulenput localvariable, add one
                        if (localVariable.props.config.moduleInput && moduleInputsIndex === -1) {
                            
                            const missingInput = {
                                argumentName: `mod.${localVariable.props.config.varName}`,
                                type: "value",
                                value: window.apm.getDefaultValueFromLocalVariable(localVariable),
                                defaultValue: window.apm.getDefaultValueFromLocalVariable(localVariable),
                                varName: null,
                                sensitiveData: localVariable.props.config.sensitiveData ? localVariable.props.config.sensitiveData : false
                            }
                            mo.props.config.inputs.push(missingInput);

                            mo.props.lastLocalChange = window.apm.timestamp;
                        }
                        // if found
                        else if (moduleInputsIndex !== -1) {
                            const equalsAsFalsey = this.getEqualsAsFalsey(mo.props.config.inputs[moduleInputsIndex].defaultValue, window.apm.getDefaultValueFromLocalVariable(localVariable));

                            if(!equalsAsFalsey){
                                mo.props.config.inputs[moduleInputsIndex].defaultValue = window.apm.getDefaultValueFromLocalVariable(localVariable);
                                mo.props.lastLocalChange = window.apm.timestamp;
                            }
                            //// hot-fix for boolean inputs with string values
                            const variableConfig = localVariable.props.config;
                            if (variableConfig.type === VariableType.boolean) {
                                const defaultValue = window.apm.getDefaultValueFromLocalVariable(localVariable);
                                if (typeof variableConfig.defaultValue !== 'boolean') {
                                    variableConfig.defaultValue = defaultValue;
                                    localVariable.props.lastLocalChange = window.apm.timestamp;
                                }

                                const variableInput = mo.props.config.inputs[moduleInputsIndex];
                                if (typeof variableInput.value !== 'boolean') {
                                    variableInput.value = defaultValue;
                                    variableInput.defaultValue = defaultValue;
                                    mo.props.lastLocalChange = window.apm.timestamp;
                                }
                            }
                                
                        }
                    
                        if (localVariable.props.usedIn) localVariable.props.usedIn=[];
                    });
                }
                if (mo.props.config.inputs) {
                    mo.props.config.inputs.forEach(input => {
                        if (mo.localVariables) {
                        
                            const localVarIndex = mo.localVariables.findIndex(lv => lv.name === input.argumentName.split('mod.')[1]);
                            if (localVarIndex === -1) {
                                const inputIndex = mo.props.config.inputs.findIndex(inp => inp.argumentName === input.argumentName);
                                mo.props.config.inputs.splice(inputIndex, 1);
                            }
                        }
                    })
                }
            });
        }
       
        serialized.components.modules.forEach((module, m) => {
            module.versions[1].flow.nodes.forEach((node, n) => {
                if (node.props.nodeTypeName === NodeTypeName.custom && node.props.config.functionType === FunctionType.custom && !node.props.config.function) {
                    serialized.components.modules[m].versions[1].flow.nodes[n].props.config.function = { script: "" };
                }
                // clean up hard-coded error from archtype
                if (node._class === DesignClass.module) {
                    const parentModule = serialized.components.modules.findIndex(k => k.id === node.props.parentComponentID);
                    if (parentModule === -1) {
                        const serializedModule = serialized.components.modules.findIndex(i => i.name === node.name);
                        if (serializedModule!==-1) {
                            node.props.parentComponentID = serialized.components.modules[serializedModule].id;
                            if (node.props.ref) {
                                node.props.ref = serialized.components.modules[serializedModule].id;
                            }
                        }
                    }
                }
                // clean up from DEVDES2-965, unused applied to prompts in flow erroneously
                if(node.props.nodeTypeName === NodeTypeName.prompt && node.props.hasOwnProperty('unused')){
                    delete node.props.unused;
                }
            });

        });
        //hot fix for objects without config
        const objects = serialized.components.objects.filter(o => !o.props.config);
        objects.forEach((object, ob) => {
            if (!object.props.config) {
                serialized.components.modules.forEach((module, m) => {
                    module.versions[1].flow.nodes.forEach((node, n) => {
                        if (node._class !== ObjectClass.data && node.props.parentComponentID === object.id) {
                            object.props.config = node.props.config;
                        }
                    });
                });
            }
        });
        serialized.components.objects.forEach((object, o) => {
            if (object.props.nodeTypeName === NodeTypeName.custom && object.props.config.functionType === FunctionType.custom && !object.props.config.function) {
                serialized.components.objects[o].props.config.function = { script: "" };
            }
        });
        // fix for exit nodes without outputs should be removed, related to 1148; fix for Prompt Object if it is marked as ExternalAudio:1403;
        serialized.components.modules.forEach(module => {
            module.versions[1].flow.nodes.forEach(node => {
                if (node._class === ObjectClass.exit && !node.props.config.outputs) {
                    node.props.config.outputs = [{ "argumentName": null, "type": ExitObjectOutputType.value, "value": null, "varName": null, "intent": null }];
                }
                else if (node._class === ObjectClass.prompt && node.props.config.isExternalAudio && node.props.config.dynamicPromptType === "EXTERNAL_AUDIO") {
                    node.props.config.externalAudioPromptType = "EXTERNAL_AUDIO";
                    delete node.props.config.externalAudioUrlVarName;
                }
            });
        });
        serialized.components.objects.forEach(o => {
            //1403 adding the deafault valur ro prompt if marked as external Audio
            if (o.props.nodeTypeName === NodeTypeName.prompt&&o.props.config.isExternalAudio&& o.props.config.dynamicPromptType==="EXTERNAL_AUDIO") {
                o.props.config.externalAudioPromptType = "EXTERNAL_AUDIO";
                delete o.props.config.externalAudioUrlVarName;
            }
            else if (o.props.nodeTypeName === NodeTypeName.exit && !o.props.config.outputs) {
                o.props.config.outputs = [{ "argumentName": null, "type": ExitObjectOutputType.value, "value": null, "varName": null, "intent": null }];
            }
        });
        //fix for nodesduplication(1108)
        serialized.components.modules.forEach(m => {
            for (let d = 0; d < m.versions[1].flow.nodes.length; d++){
                const node = m.versions[1].flow.nodes[d];
                const dupNode = m.versions[1].flow.nodes.filter(n => n.id === node.id);
                if (dupNode.length > 1) {
                    m.versions[1].flow.nodes.splice(d, 1);
                    d--;
                }
            }
            for (let li = 0; li < m.versions[1].flow.links.length; li++){
                const link = m.versions[1].flow.links[li];
                const dupLink = m.versions[1].flow.links.filter(l => l.id === link.id);
                if (dupLink.length > 1) {
                    m.versions[1].flow.links.splice(li, 1);
                    li--;
                }
            }
            for (let lv = 0; lv < m.localVariables.length; lv++){
                const localVar = m.localVariables[lv];
                const dupVar = m.localVariables.filter(v => v.id === localVar.id);
                if (dupVar.length > 1) {
                    m.localVariables.splice(lv, 1);
                    lv--;
                }
            }
        });
        //DEVDES2-1184
        serialized.components.modules.forEach(m => {
            if (m.localVariables) {
                m.localVariables.forEach(lv => {
                    if (lv.props.parentModule !== m.id) {
                    lv.props.parentModule= m.id;
                    }
                });
        }
        });

        // wizard configs
        if(serialized.client === 'InteractionsCommon'){
            // 
            serialized.wizardConfig = this.wizardConfig.getConfig(serialized);
        }
        
        // DEVDES2-1259 - Retroactively add Common Module remarking to previously imported Common Modules
        this.commonModuleRemarking(serialized);

        // hot-fix for Simultaneous-related prompt defect
        // should be removed after Simultaneous is released
        serialized.components.objects.filter(obj => obj.props.nodeTypeName === NodeTypeName.prompt && typeof obj.props.config.text === "string").forEach(applicationPrompt => {
            
            const promptUsedIn = ObjectsUsedIn.getObjectUsedIn(applicationPrompt.id, serialized);
            // fix the application prompt
            const promptText = applicationPrompt.props.config.text;

            applicationPrompt.props.config.text = {"en-US": promptText}
            applicationPrompt.props.lastLocalChange = window.apm.timestamp;


            // fix instance prompts
            // promptUsedIn[0] = usedInFlows
            promptUsedIn[0].forEach(modId => {
            
                const instancePrompts = serialized.components.modules.find(mod => mod.id === modId).versions[1].flow.nodes.filter(node => node.props.parentComponentID === applicationPrompt.id);
                
                instancePrompts.forEach(instancePrompt => {
                    const instancePromptText = instancePrompt.props.config.text;
                    instancePrompt.props.config.text = {"en-US": instancePromptText};
                    instancePrompt.props.lastLocalChange = window.apm.timestamp;

                })
                
                
            })
        })
        
        //DEVDES2-1475  adding timeoutSeconds key to Data Acess Completion Node, by default these nodes doesn't have this key when node is created
        serialized.components.modules.forEach(mod => {
            mod.versions[1].flow.nodes.forEach(node=>{
                if (node._class === ObjectClass.dataAccessCompletion && node.props.config.timeoutSeconds === undefined) {
                    node.props.config.timeoutSeconds="10";
                    //when  object is created and added to canvas and DataAccessCompletion node deoesn't have "timeoutSeconds" error is added to props removing that as the field is no longer empty
                    //the error is added only to flow instance 
                    delete node.props.error["inputValRules::timeoutSeconds::noEmpty"];
                }
                //DEVDES2-1756
                if(node.props.nodeTypeName === NodeTypeName.data && node.props.config.httpRequestTimeout === undefined){
                    node.props.config.httpRequestTimeout = 30;
                }
            })
        });
        //DEVDES2-1475  
        serialized.components.objects.forEach(obj => {
            if(obj.props.nodeTypeName===NodeTypeName.dataAccessCompletion&&obj.props.config.timeoutSeconds===undefined){
                    obj.props.config.timeoutSeconds="10";
            }
            //DEVDES2-1756
            if(obj.props.nodeTypeName === NodeTypeName.data && obj.props.config.httpRequestTimeout === undefined){
                obj.props.config.httpRequestTimeout = 30;
            }
        });
        //DEVDES2-1852 add new "dtmfLiveAgent" key to existing input objects,
        //TODO: need to remove this in future, for new input objects fix is in seed.js
        this.addDtmfLiveAgent(serialized);

        //CORE-29355
        if(serialized.client === "InteractionsCommon"){
            this.normalizeCommonModuleApplicationObjects(serialized);
        }

        this.normalizeSensitiveData(serialized);
        
        // fix if no language list defined
        if (!serialized.languages) {
            serialized.languages = [{
                language: defaultLanguage,
                voice: defaultVoiceTalent
            }];
        }
        
        for (var type in serialized.components) {
            if (serialized.components.hasOwnProperty(type)) {
                for (var i = 0; i < serialized.components[type].length; i++) {
                    this.normalizeNode(serialized.components[type][i], serialized);
                }
            }
        }

        serialized.applicationVersionLastChange  = (
            serialized.applicationVersionLastChange
            || serialized.lastSavedToRepo
            || window.apm.timestamp
        );

        /**
         * Fix just for "Progressive CustomerService" client application.
         * An specific Custom Node has several variants (instances) that are not matching
         * with the most recent one (we are going to look for the last saved change and copy/paste its values into the other ones)
         */
        if (serialized.client === "Progressive" && serialized.application === "CustomerService") {
            const customNodes = [
                {
                    name: "IS_TREATMENT_API_CALL_SUCCESSFUL",
                    variants: []
                }
            ];

            customNodes.forEach(customNode => {
                const applicationCustomNode = serialized.components.objects.find(obj => obj.name === customNode.name);
                const applicationCustomNodeId = applicationCustomNode?.id;

                // if not found, do not proceed.
                if (!applicationCustomNode || !applicationCustomNodeId) return;

                // save the application node as one variant of the custom node to fix
                customNode.variants.push(applicationCustomNode);

                // search for the instances of the custom node
                const [ usedInModuleIds ] = ObjectsUsedIn.getObjectUsedIn(applicationCustomNodeId, serialized);
                const usedInModules = serialized.components.modules.filter(mod => usedInModuleIds.includes(mod.id));
                const usedInModulesFlowNodes = usedInModules.map(mod => mod.versions[1].flow.nodes).flat();
                const customNodeInstances = usedInModulesFlowNodes.filter(flowNode => flowNode.props.parentComponentID === applicationCustomNodeId);

                // save the instances as variants
                customNode.variants.push(...customNodeInstances);

                // sort ascending order by `lastSavedChange`
                // meaning that the last element of the array is the latest or most recent updated.
                customNode.variants.sort((a, b) => a.props.lastSavedChange - b.props.lastSavedChange);

                // lets get the most recent updated custom node and update the other variants
                const latestCustomNodeVariant = customNode.variants.pop();
                const timestamp = window.apm.timestamp;

                customNode.variants.forEach(customNode => {
                    customNode.props.config.function.arguments = latestCustomNodeVariant.props.config.function.arguments;
                    customNode.props.config.function.outputs = latestCustomNodeVariant.props.config.function.outputs;
                    customNode.props.config.function.script = latestCustomNodeVariant.props.config.function.script;
                    customNode.props.config.inputs = latestCustomNodeVariant.props.config.inputs;
                    customNode.props.config.outputs = latestCustomNodeVariant.props.config.outputs;
                    customNode.props.lastLocalChange = timestamp;
                });
            });
        }

        //TODO: remove this in future, for new input objects fix is in seed.js
        const isWUCS = serialized.client === "WesternUnion" && serialized.application === "CustomerService" 
        const isMLWT = serialized.client === "MetLife" && serialized.application === "WebTech" 
        if (isWUCS || isMLWT) {
            this.changeColorSimultaneousSessionsNodes(serialized);
        }
        
        return serialized;
    }

    /** Normalize variable properties. */
    normalizeVariable(variable) {
        let updated = false;
        const { config } = variable.props;

        if ('defalutValue' in config) {
            config.defaultValue ??= config.defalutValue;
            delete config.defalutValue;
            updated = true;
        }

        if (
            config.type === VariableType.boolean
            && typeof config.defaultValue !== 'boolean'
        ) {
            config.defaultValue = false;
            updated = true;
        }

        if (updated) {
            variable.props.lastLocalChange = window.apm.timestamp;
        }
    }

    /**
     * Returns true if two values are of same value, different type
     * used in boolean contexts
     * @param {boolean/falsey} value1 
     * @param {boolean/falsey} value2 
     * @returns 
     */
    getEqualsAsFalsey(value1, value2){
        // this is a deliberate '==' operator
        return value1 == value2;

    }

    getApplicationPrompts(serialized){
        return serialized.components.objects.filter(obj => obj.props.nodeTypeName === NodeTypeName.prompt);
    }

    /**
     * This function adds "commonModuleName" and "commonModuleVersion" to properties.
     * This is for the main imported Common Module and any nested Common Modules. 
     * These properties are used in export to help PE with error logging.
     * @param {object} serialized
     */
    // TODO: this is changing config values but not updating lastLocalChange
    commonModuleRemarking(serialized) {
        serialized.components.modules.forEach((mod) => {   
            if(mod.hasOwnProperty('repoName')){
                mod.props.commonModuleName = mod.repoName;
                mod.props.commonModuleVersion = mod.props.config.version;
            }
            if(mod.versions){
                const nodeFlow = mod.versions[1].flow.nodes;

                nodeFlow.forEach(n=>{
                    const parentMod = serialized.components.modules.filter(p => p.id === n.props.parentComponentID)[0];
                    
                    if(parentMod && parentMod.hasOwnProperty('repoName')) {
                        n.props.commonModuleName = parentMod.repoName;
                        n.props.commonModuleVersion = parentMod.props.config.version;
                    }
                    
                });
            }
        });
    }

    /**
     * Normalize commen elements data shape
     * @param commonElements
     * @return {*}
     */
    normalizeCommonElements(commonElements, serialized) {
        for (var type in commonElements) {
            if (commonElements.hasOwnProperty(type)) {
                for (var i = 0; i < commonElements[type].length; i++) {
                    const cat = commonElements[type][i];
                    for (var item = 0; item < cat.items.length; item++) {
                        // TODO: need to handle commonElements, new object creation via new element dialog
                        this.normalizeNode(cat.items[item], null);
                    }
                }
            }
        }
        return commonElements;
    }
    /**
     * add new dtmfLiveAgent key to input objects for the existing objects
     * @param {*} serialized 
     */
    addDtmfLiveAgent(serialized){
        serialized.components.objects.forEach(obj=>{
            if (obj._class === ObjectClass.input && obj.props.config.dtmfLiveAgent === undefined) {
                obj.props.config.dtmfLiveAgent =  false;
            }
        })
        serialized.components.modules.forEach(module=>{
            module.versions[1].flow.nodes.forEach(node=>{
                if (node._class === ObjectClass.input && node.props.config.dtmfLiveAgent === undefined) {
                    node.props.config.dtmfLiveAgent =  false;
                }
            })
        })
    }
    /**
     * Adds scope on `props.config` for all application variables
     * See OmniChannel switch in Settings page 
     * @param {*} variables
    */
    addScopeToAppVariables(variables){
        variables.forEach(variable=>{
            if(!variable.props.config.scope){
                variable.props.config.scope = "session";
                variable.props.lastLocalChange = window.apm.timestamp;
            }
        })
    }
    /**
     * Normalize node sensitive and sensitivedata 
     * @param sensitivedata 
     */
    normalizeSensitiveData(serialized){
        const appVariables = serialized.components.variables;
        
        // objects
        serialized.components.objects.map(object => {
            const inputs = object.props.config.inputs || [];
            inputs.map(input => {
                if (input.type === "variable") {
                    const varName = input.varName.split(".")[1];
                    if (input.varName.split(".")[0] === "app") {
                        const appVariable = appVariables.find(v => v.props.config.varName === varName);
                        if (appVariable && input.sensitiveData !== appVariable.props.config.sensitiveData) {
                            input.sensitiveData = appVariable.props.config.sensitiveData;
                            object.props.lastLocalChange = window.apm.timestamp;
                        }
                    }
                }
            });
        });

        // modules
        // serialized.components.modules.map((module) => {
        //     const nodes = module.versions[1].flow.nodes || [];
        //     nodes.map(node => {
        //         const inputs = node.props.config.inputs || [];
        //         inputs.map(input => {
        //             if (input.type === "variable") {
        //                 const varName = input.varName.split(".")[1];
        //                 if (input.varName.split(".")[0] === "app") {
        //                     const appVariable = appVariables.find(v => v.name === varName);
        //                     if (appVariable && input.sensitiveData !== appVariable.props.config.sensitiveData) {
        //                         input.sensitiveData = appVariable.props.config.sensitiveData;
        //                         input.sensitive = appVariable.props.config.sensitiveData;
        //                         node.props.lastLocalChange = window.apm.timestamp;
        //                     }
        //                 }
        //             }
        //         });
        //     });
        // });
    }
    /**
     * Normalize node shape, should operate on application and instance components 
     * @param node
     */
    normalizeNode(node, serialized = null) {
        // fix if no props defined
        if (!node.props) {
            console.error("no props defined!")
            node.props = {};
        }

        // fix if no config defined
        if (!node.props.config) {
            console.error("no config defined!")
            node.props.config = {};
        }

        // fix if no nodeTypeName defined
        try {
            if (!node.props.nodeTypeName) {
                var nodeModel;
                if (node.type === "variable") {
                    nodeModel = new VariableDataModel(node.props, node.id);
                } else {
                    nodeModel = new NodeRegistry.r[node["_class"]](node.name, 0, 0, node.props, node.id);
                }
                node.props.nodeTypeName = nodeModel.props.nodeTypeName;
            }
        } catch (e) { }

        // prompt node
        if (node._class === ObjectClass.prompt) {
            // prompt node default properties
            if (!node.props.config.hasOwnProperty("bargeable")) {
                node.props.config.bargeable = "DISABLED";
            }
            if (!node.props.config.hasOwnProperty("repeatable")) {
                node.props.config.repeatable = true;
            }
            if (!node.props.config.hasOwnProperty("isReferenceable")) {
                node.props.config.isReferenceable = false;
            }
            
        }
        
    }

    /**
     * Upgrade prompt objects and their instances with channel config
     * @param {*} prompt 
     */
    udgradePromptConfigForSimultaneous(serialized) {
        //upgrade the application prompt objects
        serialized.components.objects.filter(obj => {
            return obj._class === ObjectClass.prompt;
        }).forEach(prompt => {
            this.initializePromptChannelConfig(prompt);
        });

        // upgrade all prompt node instances 
        serialized.components.modules.forEach(designModule => {
            designModule.versions[1].flow.nodes.filter(node => {
                return node._class === ObjectClass.prompt;
            }).forEach(promptInstance => {
                // upgrade the instance config
                this.initializePromptChannelConfig(promptInstance);
            });
        });

        window.apm.setSerializedChanged();
    }

    /**
     * Initialize channel configuration for legacy prompt objects
     * @param {object} prompt 
     * @returns 
     */
    initializePromptChannelConfig(prompt) {
        if (prompt.props.config.hasOwnProperty("channel") && !isEmpty(prompt.props.config.channel)) {
            return prompt;
        }

        const promptText = prompt.props.config.text;

        // for dev: prompt.props.config.text not defined, this would be a problem
        if (!promptText || typeof promptText !== "object") {
            console.error("prompt.props.config.text not defined");
            return null;
        }

        // create default channel with existing languages
        const existingPromptLanguages = Object.keys(promptText);

        // for dev: prompt text language not defined (for dev), should never occur
        if(!Object.keys(existingPromptLanguages).length){
            console.error("prompt text language not defined")
            return null;
        }
    
        prompt.props.config.channel = {}
        prompt.props.config.channel[DesignChannelType.voice] = {}
        prompt.props.config.channelLayer = DesignChannelType.voice;
        existingPromptLanguages.forEach(language => {
            prompt.props.config.channel[DesignChannelType.voice][language] = promptText[language];
        });

        prompt.props.lastLocalChange = window.apm.timestamp;

        return prompt;
    }
    

    /**
     * Get item index given value match
     * @param value
     * @param availableOptions
     * @return {number}
     */
    getIndexFromValue(value, availableOptions) {
        var idx = -1;
        for (var i = 0; i < availableOptions.length; i++) {

            if (availableOptions[i].value === value) {
                idx = i;
                break;
            }
        }
        return idx;
    }
    getItemFromValue(value, availableOptions, propertyName) {
        var filterExpression = {};

        for (var i = 0; i < availableOptions.length; i++) {

            if (availableOptions[i][propertyName] === value) {

                return availableOptions[i];
            }
        }
        return "";
    }

    /**
     * Get variable types
     * @returns {Array}
     */
    getVariableTypes() {
        return [{
            value: "ARRAY",
            name: "Array"
        },
        {
            value: "BOOLEAN",
            name: "Boolean"
        },
        {
            value: "NUMBER",
            name: "Number"
        },
        {
            value: "OBJECT",
            name: "Object"
        },
        {
            value: "STRING",
            name: "Generic String"
        },
        {
            value: "PROMPT",
            name: "Prompt"
        },
        {
            value: "MODULE",
            name: "Module"
        }
        ];
    }
    getParamTypes() {
        return [{
            value: "BOOLEAN",
            name: "Boolean"
        },
        {
            value: "MODULE",
            name: "Module"
        },
        {
            value: "NUMBER",
            name: "Number"
        },
        {
            value: "STRING",
            name: "Generic String"
        }
        ];
    }
    getVariableTypesForTabular() {
        return [
            {
                value: "BOOLEAN",
                name: "Boolean"
            },
            {
                value: "NUMBER",
                name: "Number"
            },
            {
                value: "STRING",
                name: "Generic String"
            }
        ];
    }

    /**
     * Get the index of variable type in type list
     * @param {string} value
     * @returns {number}
     */
    getVariableTypeIndexFromValue(value) {
        return this.getIndexFromValue(value, this.getVariableTypes());
    }
    getParamTypeIndexFromValue(value) {
        return this.getIndexFromValue(value, this.getParamTypes());
    }

    getNodeInputTypes() {
        var options = [
            { value: "variable", name: "Variable Reference" },
            { value: "value", name: "Value" }
        ];
        return options.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    getNodeInputTypeIndexFromValue(value) {
        return this.getIndexFromValue(value, this.getNodeInputTypes());
    }

    /**
     * Get DTMF Options
     * @returns {Array}
     */
    getDTMFOptions(intent) {
        const { intents } = window.apm.serialized.components;
        const usedDtmfInputs = new Set(intents.map(({ props }) => {
            return props.config.dtmfInput;
        }));
        return [
            ...Object.values(DtmfValue).filter((value) => {
                return (
                    value === DtmfValue.none
                    || value === intent.props.config.dtmfInput
                    || !usedDtmfInputs.has(value)
                );
            }).map((value) => {
                return {
                    name: value,
                    value
                };
            })
        ];
    }

    /**
     * Get types for Input node's input type
     * @returns {Array}
     */
    getInputTypes() {
        const types = [{
            name: "Digits",
            value: "DIGITS",
            required: true
        }, {
            name: "Dates",
            value: "DATE",
            required: true
        }, {
            name: "CVV",
            value: "CVV",
            required: true
        }, {
            name: "Phone Capture",
            value: "PHONE_CAPTURE",
            required: true
        }, {
            name: "ZIP Capture",
            value: "ZIP_CAPTURE",
            required: true
        }, {
            name: "SSN Capture",
            value: "SSN_CAPTURE",
            required: true
        }, {
            name: "Credit Card Capture",
            value: "CREDIT_CARD_CAPTURE",
            required: true
        }, {
            name: "CC Expiration date",
            value: "CC_EXPIRATION_DATE",
            required: true
        }, {
            name: "Alphanumeric Capture",
            value: "ALPHANUMERIC_CAPTURE",
            required: true
        }, {
            name: "Bank Routing Number",
            value: "BANK_ROUTING_NUMBER",
            required: true
        }, {
            name: "Postal Code capture",
            value: "POSTAL_CODE_CAPTURE",
            required: true
        }, {
            name: "DOB Capture",
            value: "DOB_CAPTURE",
            required: true
        }, {
            name: "Future Date Capture",
            value: "FUTURE_DATE_CAPTURE",
            required: true
        }, {
            name: "Time of Day Capture",
            value: "TIME_OF_DAY_CAPTURE",
            required: true
        }, {
            name: "Y/N Generic",
            value: "YN_GENERIC"
        }, {
            name: "Digit Capture",
            value: "DIGIT_CAPTURE",
            required: true
        }, {
            name: "Number Capture",
            value: "NUMBER_CAPTURE",
            required: true
        }, {
            name: "Dynamic Matching",
            value: "DYNAMIC_MATCHING",
            required: true
        }, {
            name: "Email Capture",
            value: "EMAIL_CAPTURE",
            required: true
        }, {
            name: "Address Capture",
            value: "ADDRESS_CAPTURE",
            required: true
        }, {
            name: "First Name Capture",
            value: "FIRST_NAME_CAPTURE",
            required: true
        }, {
            name: "Last Name Capture",
            value: "LAST_NAME_CAPTURE",
            required: true
        }, {
            name: "Currency Capture",
            value: "CURRENCY_CAPTURE",
            required: true
        }, {
            name: "Simple Choice",
            value: "SIMPLE_CHOICE"
        }].sort((a, b) => {
            return a.name.localeCompare(b.name);
        });

        return [
            {
                name: "None",
                value: "NONE"
            },
            ...types
        ];
    }

    /**
     * Get all module components of app
     * @returns {Array}
     */
    getModules() {
        const nodes = window.apm.serialized.components.modules;
        var modules = [];
        for (var i = 0; i < nodes.length; i++) {
            if (!nodes[i].props.isCommonModule || nodes[i].props.isRootModule) {
                modules.push({
                    name: nodes[i].name,
                    value: nodes[i].id
                });
            }
        }
        return modules.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * Get all session components of app
     * @returns {Array}
     */
    //BEN
    getSessions() {
        const nodes = window.apm.serialized.components.objects;
        var sessions = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i]._class === ObjectClass.session) {
                sessions.push({
                    name: nodes[i].name,
                    value: nodes[i].id,
                    channelName: nodes[i].props.config.channelName
                });
            }
        }
        return sessions.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    getFolders() {

        const nodes = window.apm.serialized.components.modules;
        let modules = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i].props.nodeTypeName === "Folder") {
                modules.push({
                    name: nodes[i].name,
                    value: nodes[i].id
                });
            }
        }

        return modules.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    getFoldertypes() {

        const keys = Object.keys(window.apm.serialized.folders);
        let folders = [];
        for (var i = 0; i < keys.length; i++) {

                folders.push({
                    name: keys[i],
                    value: keys[i]
                });

        }

        return folders;
    }

    getCommonModulesOptions(commonModule) {
        const mods = window.apm.serialized.components.modules.filter(mod => mod.props.moduleId === commonModule.id);
        const modules = [];
        mods.forEach(mod => {
            modules.push({
                name: mod.name,
                value: mod.id
            });
        })
        return modules.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }
    
    /**
     * If nodes are missing parent components create the object and them to serialized components
     * We are only looking for objects that are native to the common module
     * @param {*} commonModule 
     */
    normalizeCommonModuleApplicationObjects(commonModule) {
        const { timestamp } = window.apm;
        commonModule.components.modules.forEach((mod) => {
            // we don't want to look at nested common modules (only native)
            if (!mod.props.isCommonModule) {
                mod.versions[1].flow.nodes.forEach((node) => {
                    const parentObject = commonModule.components.objects.find(o=>o.id === node.props.parentComponentID && o.props.nodeTypeName === node.props.nodeTypeName);
                    if(node.type === "object" && node._class !== ObjectClass.note && node._class !== ObjectClass.begin && !parentObject){
                        let object = {
                            "id": node.props.parentComponentID,
                            "name": node.name,
                            "type": "object",
                            "props": {
                                "config":node.props.config
                            },
                            "_class": node._class,
                        };
                        if (node._class === ObjectClass.prompt) {
                            object.props.wftName = commonModule.application + "_" + object.name
                        }
                        node.props.lastLocalChange = timestamp;
                        object.props.lastLocalChange = timestamp;
                        object.props.createdTime = timestamp;
                        commonModule.components.objects.push(object);
                    }
               });
            }
       });
   }

    /**
     * Get all intent components of app
     * @returns {Array}
     */
    getIntents() {
        const nodes = window.apm.serialized.components.intents;
        var intents = [];
        for (var i = 0; i < nodes.length; i++) {
            intents.push({
                name: nodes[i].name,
                value: nodes[i].id
            });
        }
        return intents.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * Get all intent components of app that aren't imported from Common Modules
     * @returns {LegacyOption<string>[]}
     */
    getUniqueIntents() {
        const allIntents = window.apm.serialized.components.intents;
        const uniqIntents = _.uniqBy(allIntents, (e) => {return e.name});
        const intents = uniqIntents.map(int => {
            return ({
                name: int.name,
                value: int.id
            });
        });

        return intents.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }
    
    /**
     * Get all prompt components of app
     * @returns {Array}
     */
    //BEN
    getPrompts() {
        const nodes = window.apm.serialized.components.objects;
        var prompts = [];
        for (var i = 0; i < nodes.length; i++) {
            if (nodes[i]._class === ObjectClass.prompt) {
                prompts.push({
                    name: nodes[i].name,
                    value: nodes[i].id
                });
            }
        }
        return prompts.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }

    /**
     * Get the index of value in intent list
     * @param {string} value
     * @returns {number}
     */
    getIntentIndexFromValue(value) {
        const availableOptions = this.getIntents();
        var idx = -1;
        for (var i = 0; i < availableOptions.length; i++) {
            if (availableOptions[i].value === value) {
                idx = i;
                break;
            }
        }
        return idx;
    }

    /**
     * Get the index of value in unique intent list
     * @param {string} value
     * @returns {number}
     */
    getUniqueIntentIndexFromValue(value) {
        const availableOptions = this.getUniqueIntents();
        var idx = -1;
        for (var i = 0; i < availableOptions.length; i++) {
            if (availableOptions[i].value === value) {
                idx = i;
                break;
            }
        }
        return idx;
    }

    /**
     * Get reusable functions
     * @param {string} id
     */
    getFunctions(id) {
        const functions = window.apm.serialized.functions ? window.apm.serialized.functions : [];
        if (id) {
            for (var i = 0; i < functions.length; i++) {
                if (functions[i].id === id) {
                    return functions[i];
                }
            }
            return null;
        } else {
            return functions.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        }
    }
    


    /**
     * Get schemas
     * @param {string} id
     * @returns {*}
     */
    getSchemas(id) {
        const schemas = window.apm.serialized.graphQLSchemas ? window.apm.serialized.graphQLSchemas : [];
        if (id) {
            for (var i = 0; i < schemas.length; i++) {
                if (schemas[i].id === id) {
                    return schemas[i];
                }
            }
            return null;
        } else {
            return schemas.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        }
    }

    /**
     * Get CSVs
     * @param {string} id
     * @returns {*}
     */
    getCSVData(id) {
        const csv = window.apm.serialized.csvData ? window.apm.serialized.csvData : [];
        if (id) {
            for (var i = 0; i < csv.length; i++) {
                if (csv[i].id === id) {
                    return csv[i];
                }
            }
            return null;
        } else {
            return csv.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        }
    }

     /**
     * Get elements as templates. Used by NewElementDialog
     * @param {string} type Element type
     * @returns {*}
     */
      getCommonElements(type) {
        const simultaneousEnabled = window.apm.serialized.omniChannel.isOmniChannel;
        const bannerFlag = featureFlagService.isFeatureOn(UserFeatureFlag.banner);
        const richTextFlag = featureFlagService.isFeatureOn(UserFeatureFlag.richText)
        let commonElements = _.cloneDeep(this.commonElements[type]);
        
        if (type === "objects") {
            // filter-out session nodes if simultaneous feature flag is not enabled
            if (!simultaneousEnabled) {
                commonElements[0].items = this.commonElements[type][0].items.filter(el => el.name !== "START_SIMULTANEOUS_SESSION" && el.name !== "SIMULTANEOUS_SESSION_COMPLETION");
            }
            //add channel config to prompts if simultaneous is enabled
            else {
                commonElements[0].items = this.commonElements[type][0].items;
                const prompt = this.commonElements[type][0].items.find(i => i._class === ObjectClass.prompt);
                this.initializePromptChannelConfig(prompt);
            }

            // [CORE-33290] Don't show save prompt objects unless this app already has one of them.
            if (!window.apm.appHasSavePromptObjects()) {
                const savePromptsObjects = [ObjectClass.savePrompt, ObjectClass.playSavedPrompt];
                commonElements[0].items = commonElements[0].items.filter((x) => !savePromptsObjects.includes(x._class));
            }

            // [CORE-32036] Don't show rich text objects unless the feature flag is enabled.
            if (!richTextFlag) {
                const richTextObjects = [ObjectClass.richText];
                commonElements[0].items = commonElements[0].items.filter((x) => !richTextObjects.includes(x._class));
            }

            // [CORE-33213] Don't show banner message objects unless the feature flag is enabled.
            if (!bannerFlag) {
                const bannerObjects = [ObjectClass.banner];
                commonElements[0].items = commonElements[0].items.filter((x) => !bannerObjects.includes(x._class));
            }
        }

        return commonElements || [];
    }

    /**
     * Setter for Common Modules reference
     * @param {*} version data 
     */
    setCommonModuleElements(items){
        this.commonElements['modules'] = [{category: 'common', items: items || []}]
    }

    /**
     * Get variable suggestions for query
     * @param {string} query
     * @param {string} filter Scope filter
     * @returns {Record<string, DesignVariable[]>}
     */

    // TODO: refactor the if/elseif/else structure of this method and have only one return statement 
    getVariableSuggestions(query, filter = null, reference = null) {
        var result = {};
        result[Constant.k.SCOPE_APPLICATION] = [];
        result[Constant.k.SCOPE_MODULE] = [];

        if (filter === Constant.k.SCOPE_APPLICATION) {
            result[Constant.k.SCOPE_APPLICATION] = window.apm.searchVariable(query, reference);
        } else if (filter === Constant.k.SCOPE_MODULE) {
            result[Constant.k.SCOPE_MODULE] = window.apm.getCurrentDiagramWidget().searchVariable(query, reference);
        } else {
            if (!query) {
                return result;
            }
            result[Constant.k.SCOPE_APPLICATION] = window.apm.searchVariable(query, reference);
            result[Constant.k.SCOPE_MODULE] = window.apm.getCurrentDiagramWidget().searchVariable(query, reference);
        }
        return result;
    }

    getAsyncDataAccessNodeInstances() {
        const modules = window.apm.serialized.components.modules;
        const relevantModules = modules.filter((mod) => mod.versions && mod.versions["1"])
            .map((mod) => ({
                id: mod.id,
                name: mod.name,
                flow: mod.versions["1"].flow
            }));

        const relevantDataAccessNodes = relevantModules.reduce(function (acc, mod) {
            if (!mod.flow || !mod.flow.nodes || !mod.flow.nodes.length) {
                return acc;
            }

            mod.flow.nodes.filter(node => node._class == ObjectClass.data)
                .filter(node => node.props.config.asynchronous)
                .forEach(function (node) {
                    let dataAccessNodeEntry = {
                        name: `${node.name} (${mod.name})`,
                        value: node.id,
                        node: node
                    };
                    acc.push(dataAccessNodeEntry);
                });

            return acc;
        }, []);
        return relevantDataAccessNodes;
    }

    // As part of the ticket CORE-33819, we are removing all pending pendo information stored in the browser
    removeFromLocalAndSessionStoragePendo() {
        const localStorageKeys = Object.keys(localStorage);
        const pendoLocalStorageKeys = localStorageKeys.filter(key => key.includes("pendo"));
        const sessionStorageKeys = Object.keys(sessionStorage);
        const pendoSessionStorageKeys = sessionStorageKeys.filter(key => key.includes("pendo"));
        
        pendoLocalStorageKeys.forEach(key => localStorage.removeItem(key));
        pendoSessionStorageKeys.forEach(key => sessionStorage.removeItem(key));
    }

    // As part of the ticket CORE-34104, we are changing the color of the Simultaneous Sessions nodes to YELLOW
    changeColorSimultaneousSessionsNodes(serialized) {
        try {
            const filterCriteria = (obj) =>
                obj._class === ObjectClass.session ||
                obj._class === ObjectClass.sessionCompletion;
        
            const objects = serialized.components.objects.filter(filterCriteria);
            const nodes = serialized.components.modules
                .map((mod) => mod.versions["1"].flow.nodes)
                .flat()
                .filter(filterCriteria);
        
            [...objects, ...nodes].forEach((obj) => {
                obj.props.colorClass = "yellow";
            });
        } catch (error) {
            console.error(error);
        }
    }
}